import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { MetadataSchemaField } from '@/types/metadata.types';
import { useResolvedFieldOptions } from '../useResolvedFieldOptions';

interface MultiSelectFieldProps {
  field: MetadataSchemaField;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
}

export default function MultiSelectField({
  field,
  value,
  onChange,
  error,
  disabled,
}: MultiSelectFieldProps) {
  const selectedValues = Array.isArray(value) ? (value as string[]) : [];
  // Resolves MANUAL options or a catalog-backed data source transparently.
  const { options, isLoading } = useResolvedFieldOptions(field);

  const handleToggle = (optionValue: string, checked: boolean) => {
    if (checked) {
      onChange([...selectedValues, optionValue]);
    } else {
      onChange(selectedValues.filter((v) => v !== optionValue));
    }
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-2">Loading…</div>;
  }

  if (options.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-2">
        No options available
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${error ? 'ring-1 ring-destructive rounded-md p-2' : ''}`}>
      {options.map((opt) => (
        <div key={opt.value} className="flex items-center space-x-2">
          <Checkbox
            id={`${field.fieldKey}-${opt.value}`}
            checked={selectedValues.includes(opt.value)}
            onCheckedChange={(checked) => handleToggle(opt.value, !!checked)}
            disabled={disabled}
          />
          <Label
            htmlFor={`${field.fieldKey}-${opt.value}`}
            className="text-sm font-normal cursor-pointer"
          >
            {opt.label}
          </Label>
        </div>
      ))}
    </div>
  );
}
